import { Button, Card, CardContent, Spinner, type Step, Steps } from '@crm/ui';
import { ArrowLeft, ArrowRight, CreditCard, MessageSquare, Phone } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlanOffer } from '@/modules/billing/components/plan-offer';
import { BILLING_ROUTES } from '@/modules/billing/constants';
import type { PhoneLine } from '../api';
import { useWhatsappUnit } from '../hooks';
import { ChargeStep } from './charge-step';
import { ConnectStep } from './connect-step';
import { LineStep } from './line-step';
import { UnitHeader } from './unit-header';

const PASSOS: readonly Step[] = [
  { key: 'numero', label: 'Numero', icon: Phone },
  { key: 'cobranca', label: 'Cobranca', icon: CreditCard },
  { key: 'status', label: 'Status', icon: MessageSquare },
];

function passoAlcancado(linha: PhoneLine | null): number {
  if (!linha) return 0;

  return linha.openCharge ? 1 : 2;
}

export function UnitSetup({ unitId }: { unitId: string }) {
  const { carregando, unidade, linha } = useWhatsappUnit(unitId);
  const [passo, setPasso] = useState(0);

  if (carregando) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (!unidade) return <UnitHeader title="Unidade nao encontrada" />;

  const maximo = passoAlcancado(linha);
  const atual = Math.min(passo, maximo);

  return (
    <div className="space-y-4">
      <UnitHeader
        title={unidade.name}
        description="Contrate a linha e conecte o numero ao WhatsApp da unidade."
      />

      <Steps
        passos={PASSOS}
        atual={atual}
        onSelect={(indice) => setPasso(Math.min(indice, maximo))}
      />

      <Card variant="flush">
        <CardContent className="py-6">
          {atual === 0 ? <LineStep unitId={unidade.id} linha={linha} /> : null}

          {atual === 1 && linha ? <ChargeStep linha={linha} onPaid={() => setPasso(2)} /> : null}

          {atual === 2 && linha ? <ConnectStep linha={linha} unit={unidade} /> : null}
        </CardContent>
      </Card>

      {atual === 1 ? <PlanOffer /> : null}

      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" disabled={atual === 0} onClick={() => setPasso(atual - 1)}>
          <ArrowLeft className="h-4 w-4" />
          Anterior
        </Button>

        {atual === PASSOS.length - 1 ? (
          <Button asChild variant="outline">
            <Link to={BILLING_ROUTES.unit(unidade.id)}>
              Ver resumo
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        ) : (
          <Button variant="outline" disabled={atual >= maximo} onClick={() => setPasso(atual + 1)}>
            Proximo
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
